<template lang="pug">
.tab-container(:class="[theme && 'theme-' + theme, sticky && 'tabs-sticky']" role="tablist")
  .tab-header
    slot(name="header")
      h1.tabs-header(v-if="title && topHeading") {{ __(title, 'auto') }}
      h2.tabs-header(v-else-if="title") {{ title }}
    .fill(
      v-if="title || align === 'right' || $slots.header"
    )
    .tabs.custom-scroll.no-pad(v-drag-scroll :class="{ 'desktop-scroll': desktopScroll }")
      button.a11y.tab-title(
        v-for="tab in tabs"
        role="tab"
        tabindex="0"
        :disabled="activeTab ? tab !== activeTab : filterTab(tab)"
        :aria-selected="modelValue === tab"
        :key="tab"
        :class="modelValue === tab && 'active'"
        @click="change(tab)"
      ) {{ __(tab, 'auto') }}
</template>
<script setup lang="ts">
import { useRogue } from '../../js/rogue'

const props = withDefaults(defineProps<{
  tabs?: string[],
  theme?: string | null,
  modelValue?: string | null,
  align?: string | null,
  sticky?: boolean,
  title?: string | null,
  activeTab?: string | null,
  topHeading?: boolean,
  tabStatus?: { name: string, disabled: boolean }[],
  desktopScroll?: boolean
}>(), {
  tabs          : () => [],
  theme         : null,
  modelValue    : null,
  align         : null,
  sticky        : false,
  title         : null,
  activeTab     : null,
  topHeading    : false,
  tabStatus     : () => [],
  desktopScroll : false,
})

const emit = defineEmits(['update:modelValue'])

const rogue = useRogue()

function change(val: string): void {
  if (props.modelValue === val) {
    return
  }
  emit('update:modelValue', val)
  if (rogue) {
    rogue.track.track({ type: 'changeTab', tab: val })
  }
}

function filterTab(tab: string): boolean{
  if(!props.tabStatus.length) {
    return false
  }
  const filteredTab = props.tabStatus.find((item) => item.name == tab )
  
  return filteredTab ? filteredTab.disabled : false
}
</script>
<style lang="scss" scoped>

.tab-container {
  padding: var(--tabs-container-padding, 0);
}

.tabs {
  display: flex;
  justify-content: var(--tabs-justify, initial);
  @media(max-width: $small-desktop){
    overflow-x: auto;
  }
  &.desktop-scroll {
    overflow-x: auto;
  }
}

.theme-events {
  .tab-header {
    .tabs {
      overflow-x: auto;
    }
  }
}

h2 {
  margin-bottom: 0;
}

.tabs-sticky {
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 1;
}

.tab-header {
  display: flex;
  border-bottom: var(--tabs-header-border, #{$border-width-thin} solid #{$color-gray-light});
  margin-bottom: $space-s;
  justify-content: var(--tabs-header-justify, initial);
}

.tabs-header {
  color: $color-black;
}

.fill {
  flex-grow: 1;
  display: var(--tabs-fill-display, block);
}

.tab-title {
  padding: var(--tabs-title-padding, #{$space-s} #{$space-s} #{$space-xs});
  font-size: var(--tabs-title-font-size, #{$size-xs});
  margin: var(--tabs-title-margin, initial);
  text-transform: var(--tabs-title-text-transform, none);
  border-right-width: 0;
  color: $color-gray-x-dark;
  background-color: inherit;
  cursor: pointer;
  border-bottom: $border-width-thick solid transparent;

  &:hover, &.active {
    color: $color-black;
    background-color: inherit;
    border-bottom: $border-width-thick solid $color-red-dark;
  }
  &:disabled {
    &:hover {
      color: $color-gray-x-dark;
      border-bottom: $border-width-thick solid transparent;
    }
  }
}

@include all-desktop() {
  .tab-header {
    display: flex;
    align-items: center;
  }

  h2 {
    margin-bottom: 0;
  }
}

.theme-player {
  .tab-header {
    background-color: $color-gray-x-light;
    margin-bottom: 0;
  }

  .tab-title {
    font-weight: $weight-bold;
    color: $color-black-light;
  }
}

.theme-cms,
.theme-cms-light,
.theme-events,
.theme-app,
.theme-outline,
.theme-dark,
.theme-bold {
  .tab-header {
    background-color: $color-gray-light;
    display: flex;
    justify-content: center;
    align-items: center;

    @include not-desktop() {
      flex-wrap: wrap;
    }

    .tabs-header {
      width: 100%;
      text-align: center;
      padding: $space-xs 0;

      @include all-desktop() {
        width: auto;
        text-align: left;
      }
    }

    .tabs {
      padding-left: 0;
    }

    .tab-title {
      color: $color-black;
      background-color: $color-gray-light;
      margin: 0 $space-s;
      font-weight: $weight-bold;
      text-transform: var(--tabs-title-text-transform, uppercase);
      white-space: nowrap;
    }
  }

  &.theme-cms,
  &.theme-cms-light,
  &.theme-events,
  &.theme-outline,
  &.theme-dark,
  &.theme-bold {
    .tabs-header {
      color: $color-black;
    }
  }

  &.theme-cms-light {
    .tab-header {
      background-color: transparent;
    }
  }

  &.theme-events {
    .tab-title {
      font-family: $font-condensed;
      background: inherit;
      font-size: $size-l;
      font-weight: 700;
      padding: 1em $space-s calc(1em - #{$space-xxxs});
    }
  }

  &.theme-bold {
    .tab-header {
      border-bottom: var(--tabs-header-border, none);
      background-color: transparent;
      justify-content: var(--tabs-header-justify, flex-start);
      width: 100%;

      .tab-title {
        margin: var(--tabs-title-margin, 0 32px 16px 0);
        padding: var(--tabs-title-padding, 0 0 4px 0);
        background-color: transparent;
        font-size: 16px;
      }
    }

    font-family: $font-condensed;
    padding: 0;
    text-transform: uppercase;
    font-size: $size-l;
    font-weight: 700;
  }

  &.theme-outline, &.theme-dark {
    border: 2px solid $color-black;
    border-bottom: 0;

    .tab-header {
      background-color: transparent;
      border: none;
      margin: 0;
    }

    .tabs {
      min-width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      color: $color-white;
      background-color: $color-black;
    }

    .tab-title {
      flex-grow: 1;
      margin: 0;
      border: none;
      width: 100%;
      font-size: $size-s;
      background-color: $color-white;
      color: $color-black;
      border-bottom: 2px solid $color-black;
      text-align: center;
      white-space: nowrap;
      padding-right: $space-xxxs;
      padding-left: $space-xxxs;
      font-family: $font-condensed;

      &:first-child {
        border-left: none;
      }

      &.active, &:hover {
        background-color: $color-black;
        color: $color-white;
      }
    }

    @media (min-width: $tablet) {
      .tabs {
        flex-direction: row;
      }

      .tab-title {
        border-left: 2px solid $color-black;

        &:first-child {
          border-left: none;
        }
      }
    }
  }

  &.theme-dark {
    border: none;

    .tab-title {
      background-color: $color-gray-x-dark;
      color: $color-white;
      border: none;
      padding: $size-m;
    }
  }

  &.theme-app {
    margin-bottom: -$border-width-thin;

    .tab-header {
      background-color: $color-gray-x-light;
      margin-bottom: 0;
    }

    @include mobile(){
      .tabs {
        flex-grow: 1;
      }
    }

    .tab-title {
      opacity: $opacity-disabled;
      flex-grow: 1;
      margin: 0;
      text-align: center;

      &:hover, &.active {
        opacity: 1;
        border-bottom: $border-width-thick solid $color-black-light;
      }
    }
  }
}
</style>

<style lang="scss" scoped target="app">
// Mobile app-specific tab styling for challenges CMS pages (FAQ, etc.)
// These themes (outline/dark) are primarily used in challenges CMS content
.platform-ios, .platform-android {
  .theme-outline, .theme-dark {
    border: none;

    .tab-header {
      display: flex;
      flex-shrink: 0;
      border-bottom: 1px solid $color-gray-light;
      background: $color-white;
      position: sticky;
      top: 0;
      z-index: 10;
      min-height: 48px;
      margin-bottom: 0;
    }

    .tabs {
      display: flex;
      flex-direction: row;
      width: 100%;
      background-color: $color-white;
    }

    .tab-title {
      flex: 1;
      padding: $space-s;
      text-align: center;
      background: transparent;
      border: none;
      font-family: $font-condensed;
      font-size: $size-s;
      font-weight: bold;
      text-transform: uppercase;
      color: $color-gray-dark;
      cursor: pointer;
      position: relative;
      line-height: 1.2;
      min-height: 48px;
      margin: 0;
      white-space: normal;

      &:hover {
        color: $color-black;
        background: transparent;
        border: none;
      }

      &.active {
        color: $color-black;
        background: transparent;
        border: none;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: $color-red;
        }
      }
    }
  }

  .tab-title:hover:not(.active) {
    color: $color-gray-dark;
    border-bottom-color: transparent;
  }
}
</style>
